import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_gradeLevelMutation,
  useGET_gradeLevelByIdQuery,
  usePOST_gradeLevelMutation,
  usePUT_gradeLevelMutation,
} from 'redux/api/gradeLevelsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import SelectField from 'components/atoms/fields/SelectField'
import InputField from 'components/atoms/fields/InputField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/gradeLevelModel'
import { crudColorMap, crudIconMap, gradeSelectData, translateGradeLevel } from 'utils/helpers'

import { ActionType, IGroupData } from 'types'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const GradeLevelForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const backToSlug = '/school'
  const editSlug = `/grade-levels/edit/${id}`

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.grade-levels')}`,
    entityId: id,
    getEntityQuery: useGET_gradeLevelByIdQuery,
    createEntityMutation: usePOST_gradeLevelMutation,
    editEntityMutation: usePUT_gradeLevelMutation,
    deleteEntityMutation: useDELETE_gradeLevelMutation,
  })

  const { title, grade_level } = formModel()
  const serverValues = entity ? {
    ...entity,
    grade_level: {
      id: entity.grade_level,
      title: translateGradeLevel(entity.grade_level, true)
    }
  } : initialValues

  return (
    <Formik
      enableReinitialize
      initialValues={serverValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: any = {
          title: values.title,
          grade_level: values?.grade_level?.id,
        }
        handleSubmit(action, id, data)
      }}
    >
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>

          <Grid item xs={12} lg={3}>
            <SelectField
              fieldProps={{ ...grade_level }}
              apiCall={gradeSelectData}
              disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} lg={9}>
            <InputField fieldProps={{ ...title }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  )
}
export default GradeLevelForm
